import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { api } from "../api/client";
import type { StateStatusOut } from "../api/types";
import { Figure, Figures } from "../components/Figures";
import { PageHeader } from "../components/PageHeader";
import { UsMap } from "../components/UsMap";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { type ResolvedTheme, useTheme } from "../hooks/useTheme";
import { STATE_NAMES, US_STATES, fmtDate, fmtNum, fmtRelativeDays } from "../lib/format";
import { UNSUPPORTED } from "../lib/unavailable";

type Health = "operational" | "failing" | "unsupported" | "never";

// Mirrors SCRAPER_SUCCESS_STATUSES in warn_v2/db/models.py: not_modified means
// the source was reachable but unchanged since the last scrape — a success.
const SUCCESS_STATUSES = new Set(["ok", "not_modified"]);

const BADGE: Record<Health, { label: string; className: string }> = {
  operational: { label: "Operational", className: "badge-green" },
  failing: { label: "Failing", className: "badge-red" },
  unsupported: { label: "No public source", className: "badge-slate" },
  never: { label: "Never run", className: "badge-slate" },
};

// Map fills matching the badge hues. "Operational" is the normal state of 48
// states, so it takes a quiet sage that sits in the warm palette; failing
// keeps a saturated red so a problem state is the most salient thing on the map.
const HEALTH_FILLS: Record<ResolvedTheme, Record<Health, string>> = {
  light: {
    operational: "#bcd3b4",
    failing: "#e5484d",
    unsupported: "#e1e0d9", // slate-200
    never: "#e1e0d9",
  },
  dark: {
    operational: "#3e5c3f",
    failing: "#e5484d",
    unsupported: "#2c2c2a", // slate-800
    never: "#2c2c2a",
  },
};

// Failing first (that's the point of the page), then operational, then the rest.
const SEVERITY: Record<Health, number> = {
  failing: 0,
  operational: 1,
  unsupported: 2,
  never: 2,
};

/** Compact "2008–2026" span of a state's notice data, null when it has none. */
function coverage(run: StateStatusOut | undefined): string | null {
  if (!run?.first_notice_date || !run.last_notice_date) return null;
  const from = run.first_notice_date.slice(0, 4);
  const to = run.last_notice_date.slice(0, 4);
  return from === to ? from : `${from}–${to}`;
}

export function StatusPage() {
  useDocumentTitle("Scraper status — WARN Index");
  const { resolved } = useTheme();
  const healthFills = HEALTH_FILLS[resolved];

  const statusQuery = useQuery({
    queryKey: ["scraper-status"],
    queryFn: () => api.listScraperStatus(),
  });

  const byState = new Map((statusQuery.data ?? []).map((s) => [s.state, s]));

  const rows = US_STATES.map((code) => {
    const run = byState.get(code);
    let health: Health;
    if (code in UNSUPPORTED) health = "unsupported";
    else if (!run) health = "never";
    else if (SUCCESS_STATUSES.has(run.last_status)) health = "operational";
    else health = "failing";
    return { code, name: STATE_NAMES[code], run, health };
  }).sort(
    (a, b) =>
      SEVERITY[a.health] - SEVERITY[b.health] || a.name.localeCompare(b.name),
  );

  const counts = {
    operational: rows.filter((r) => r.health === "operational").length,
    failing: rows.filter((r) => r.health === "failing").length,
    other: rows.filter((r) => r.health === "unsupported" || r.health === "never")
      .length,
  };

  return (
    <div className="space-y-6">
      <PageHeader
        kicker="Data pipeline"
        title="Scraper status"
        lede={
          <>
            Health of each state scraper and when it last successfully updated. Scrapers run
            daily; a state is &ldquo;operational&rdquo; when its most recent run succeeded.
          </>
        }
      />

      <Figures cols={3}>
        <Figure label="Operational" value={statusQuery.data ? fmtNum(counts.operational) : null} />
        <Figure
          label="Failing"
          value={statusQuery.data ? fmtNum(counts.failing) : null}
          accent={counts.failing > 0}
        />
        <Figure label="No public source / never run" value={statusQuery.data ? fmtNum(counts.other) : null} />
      </Figures>

      {statusQuery.isLoading && (
        <div className="card text-center text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      )}

      {/* Don't render the map until data arrives — with no data every state
          would confidently show as never run. */}
      {statusQuery.data && (
        <div className="card">
          <div className="mx-auto max-w-4xl">
            <UsMap
              framed={false}
              fills={Object.fromEntries(rows.map((r) => [r.code, healthFills[r.health]]))}
              srLabel="Map of scraper health by state. The table below contains the same data as accessible links."
              tooltip={(code) => {
                const r = rows.find((row) => row.code === code);
                if (!r) return null;
                return (
                  <>
                    <div className="font-medium text-slate-900 dark:text-slate-100">{r.name}</div>
                    <div className="text-slate-600 dark:text-slate-400">{BADGE[r.health].label}</div>
                    {r.run?.last_success_at && (
                      <div className="text-slate-600 dark:text-slate-400">
                        Last success {fmtRelativeDays(r.run.last_success_at)}
                      </div>
                    )}
                    {coverage(r.run) && (
                      <div className="text-slate-600 dark:text-slate-400">
                        Notices {coverage(r.run)}
                      </div>
                    )}
                  </>
                );
              }}
              legend={
                <>
                  {(
                    [
                      ["operational", "Operational"],
                      ["failing", "Failing"],
                      ["unsupported", "No public source / never run"],
                    ] as const
                  ).map(([health, label]) => (
                    <span key={health} className="flex items-center gap-1.5">
                      <span
                        className="inline-block h-3 w-3 rounded-sm"
                        style={{ backgroundColor: healthFills[health] }}
                      />
                      {label}
                    </span>
                  ))}
                </>
              }
            />
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <table className="data-table w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-950 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2 font-medium">State</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Notice coverage</th>
              <th className="px-3 py-2 font-medium">Last successful scrape</th>
              <th className="px-3 py-2 font-medium">Last run</th>
              <th className="px-3 py-2 font-medium">New / scraped</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((r) => {
              const badge = BADGE[r.health];
              const reason = UNSUPPORTED[r.code];
              return (
                <tr key={r.code} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-3 py-2 align-top" data-label="State">
                    <Link
                      to="/states/$state"
                      params={{ state: r.code }}
                      className="font-medium text-slate-900 hover:underline dark:text-slate-100"
                    >
                      {r.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2 align-top" data-label="Status">
                    <span
                      className={badge.className}
                      title={reason ?? r.run?.error ?? undefined}
                    >
                      {badge.label}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-top" data-label="Notice coverage">
                    {coverage(r.run) ? (
                      <span
                        title={`${fmtDate(r.run?.first_notice_date)} – ${fmtDate(r.run?.last_notice_date)}`}
                      >
                        {coverage(r.run)}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top" data-label="Last successful scrape">
                    {r.run?.last_success_at ? (
                      <span>
                        {fmtDate(r.run.last_success_at)}{" "}
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          ({fmtRelativeDays(r.run.last_success_at)})
                        </span>
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top" data-label="Last run">
                    {r.run ? (
                      fmtDate(r.run.last_run_at)
                    ) : (
                      <span className="text-slate-400 dark:text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top" data-label="New / scraped">
                    {r.run && r.run.rows_scraped != null ? (
                      <span>
                        {fmtNum(r.run.rows_new)} / {fmtNum(r.run.rows_scraped)}
                      </span>
                    ) : (
                      <span className="text-slate-400 dark:text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400">
        “No public source” marks jurisdictions with no scrapable WARN listing
        (state law or no online source) — hover the badge for details. Counts
        cover all {US_STATES.length} US jurisdictions (50 states + DC).
      </p>
    </div>
  );
}
